// Copyright (c) StrangeBee 2024

import { Image, Space } from 'antd';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import useValidateForm from '@Hooks/useValidateForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import {
    NotificationInput,
    NotificationOutput,
    NotificationTriggerOutput,
    NotifierOutput,
    Notifiers,
} from 'thehive-sdk';
import { NotifierManage } from './NotifierManage/NotifierManage';
import THSelector from '@Molecules/THSelector/THSelector';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THListChoice } from '@Molecules/Inputs/ThListChoice/THListChoice';
import { useLocalMessage } from '@Helpers/locale';
import { notifierList, NotifiersMap, triggers } from '@Constants/notification';
import { classList } from '@Helpers/className';
import { StatusContext } from '@Context/StatusProvider';
import { ScopeContext } from '@Context/ScopeProvider';
import AceEditor from 'react-ace';

import './NotificationManage.scss';

interface NotificationManageProps {
    onFinish: (notification: NotificationInput) => Promise<any>;
    triggerElem?: ReactElement;
    defaultValues?: NotificationOutput;
    notificationNames?: string[];
    notificationCapability?: string[];
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

interface NotificationFormValues {
    name: string;
    trigger: { name: NotificationTriggerOutput; filter?: any };
    notifiers: NotifierOutput[];
    delegate: boolean;
    enabled?: boolean;
}

export const NotificationManage = ({
    onFinish,
    triggerElem,
    defaultValues,
    notificationNames,
    notificationCapability = [],
    visible,
    onVisibleChange,
}: NotificationManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!defaultValues,
        fieldsTouchedInit: !!defaultValues,
    });
    const [activeNotifier, setActiveNotifier] = useState<Notifiers>();
    const [showNotifiers, setShowNotifiers] = useState(!defaultValues);
    const { hasCortex } = useContext(StatusContext);

    const { responderList, analyzerList } = useContext(ScopeContext);

    const onFinishHandler = ({ trigger, ...values }: NotificationFormValues): Promise<any> => {
        let jsonParsed = null;

        try {
            jsonParsed = JSON.parse(trigger.filter || {});
        } catch (e) {
            jsonParsed = {};
        }

        return onFinish({
            ...values,
            hostRestriction: [],
            trigger: {
                name: trigger.name,
                filter: trigger.name === 'FilteredEvent' ? jsonParsed : undefined,
            },
        });
    };

    const isEmptyResponderList = useMemo(() => !responderList.length, [responderList]);
    const isEmptyAnalyzerList = useMemo(() => !analyzerList.length, [analyzerList]);

    return (
        <THDrawer
            title={!!defaultValues ? i('notification.form.title.edit') : i('notification.form.title.create')}
            triggerElem={triggerElem}
            className="drawer-form"
            width="55vw"
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <THForm<NotificationFormValues>
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        enabled: true,
                        ...defaultValues,
                        delegate: defaultValues ? defaultValues.delegate : false,
                        trigger: {
                            name: defaultValues?.trigger?.name || 'AnyEvent',
                            filter: JSON.stringify(defaultValues?.trigger.filter, null, 4),
                        },
                        notifiers: defaultValues?.notifiers.map((notifier) => {
                            const notif = notifierList.find(({ name }) => name === notifier.name);

                            return {
                                ...notifier,
                                enabled:
                                    notificationCapability.some((capability) =>
                                        notif?.capabilities?.includes(capability),
                                    ) &&
                                    (notifier.enabled || notifier.enabled === undefined),
                            };
                        }),
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values)
                            .then(() => {
                                toggleDrawer();
                                setFieldsTouched(false);
                            })
                            .catch((e) => e);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    toggleDrawer();
                                    setFieldsTouched(false);
                                    setShowNotifiers(!defaultValues);
                                }}
                                disabled={!isValid}
                            />
                        }
                    >
                        <THForm.Item
                            name="name"
                            label={i('notification.form.name.label')}
                            rules={[
                                {
                                    required: true,
                                    validator: (_, value) => {
                                        if (notificationNames?.includes(value) || value === '') {
                                            return Promise.reject(i('notification.form.name.already-exists'));
                                        }
                                        if (!value?.trim()) {
                                            return Promise.reject(i('notification.form.name.required'));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <THInput disabled={!!defaultValues} placeholder={i('notification.form.name.placeholder')} />
                        </THForm.Item>

                        <THForm.Item
                            name="delegate"
                            label={i('notification.form.delegate.help')}
                            valuePropName="checked"
                        >
                            <THSwitch
                                onChange={(checked) => {
                                    const notifiers = (form.getFieldValue('notifiers') || []) as NotifierOutput[];
                                    const trigger = form.getFieldValue('trigger');

                                    form.setFieldsValue({
                                        notifiers: notifiers.map((notifier) => {
                                            const notifierFiltered = notifierList.find(
                                                ({ name }) => name === notifier.name,
                                            );

                                            if (!notifier.enabled) {
                                                return notifier;
                                            }
                                            return {
                                                ...notifier,
                                                enabled: checked && notifierFiltered?.isDelegate,
                                            };
                                        }),
                                        trigger: {
                                            ...trigger,
                                            name:
                                                ['AnyEvent', 'ObservableCreated'].includes(trigger.name) && checked
                                                    ? 'AlertCreated'
                                                    : trigger.name,
                                        },
                                    });
                                }}
                            />
                        </THForm.Item>

                        <THForm.Item<NotificationFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.delegate !== next.delegate}
                        >
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name={['trigger', 'name']}
                                    label={i('notification.form.trigger.label')}
                                    rules={[{ required: true }]}
                                >
                                    <THSelect
                                        placeholder={i('notification.form.trigger.placeholder')}
                                        onSelect={(value: any) => {
                                            const notifiers = (form.getFieldValue('notifiers') ||
                                                []) as NotifierOutput[];

                                            form.setFieldsValue({
                                                notifiers: notifiers.map((notifier) => {
                                                    const notifierFiltered = notifierList.find(
                                                        ({ name }) => name === notifier.name,
                                                    );

                                                    return {
                                                        ...notifier,
                                                        enabled: notifierFiltered?.triggerExclude?.includes(
                                                            value as any,
                                                        )
                                                            ? false
                                                            : notifier.enabled,
                                                    };
                                                }),
                                            });
                                        }}
                                    >
                                        {Object.entries(triggers).reduce((acc, [entity, triggers = []]) => {
                                            const triggersFiltered = triggers
                                                .sort((a, b) => a.localeCompare(b))
                                                .filter(
                                                    (name) =>
                                                        !(
                                                            ['AnyEvent', 'ObservableCreated'].includes(name) &&
                                                            getFieldValue('delegate')
                                                        ),
                                                );

                                            if (!triggersFiltered.length) {
                                                return acc;
                                            }

                                            return [
                                                ...acc,
                                                <THSelect.OptGroup key={entity} label={i(`object-types.${entity}`)}>
                                                    {triggersFiltered.map((trigger) => (
                                                        <THSelect.Option value={trigger} key={trigger}>
                                                            {trigger}
                                                        </THSelect.Option>
                                                    ))}
                                                </THSelect.OptGroup>,
                                            ];
                                        }, [] as ReactElement[])}
                                    </THSelect>
                                </THForm.Item>
                            )}
                        </THForm.Item>

                        <THForm.Item<NotificationFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.trigger?.name !== next.trigger?.name}
                        >
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name={['trigger', 'filter']}
                                    hidden={getFieldValue(['trigger', 'name']) !== 'FilteredEvent'}
                                    rules={[
                                        {
                                            validator: (_, value = '') => {
                                                try {
                                                    value && JSON.parse(value);
                                                    return Promise.resolve();
                                                } catch (e) {
                                                    return Promise.reject(i('notification.form.filter.error'));
                                                }
                                            },
                                        },
                                    ]}
                                >
                                    <AceEditor
                                        width="100%"
                                        height="15rem"
                                        mode="json"
                                        theme="tomorrow"
                                        className={classList('th-text-area__helper')}
                                        placeholder={i('notification.notifiers.template.placeholder')}
                                    />
                                </THForm.Item>
                            )}
                        </THForm.Item>

                        <THForm.Item
                            name="enabled"
                            label={i('notification.form.enabled.label')}
                            valuePropName="checked"
                        >
                            <THSwitch />
                        </THForm.Item>

                        <THForm.List
                            name="notifiers"
                            rules={[
                                {
                                    validator: (_, value = []) => {
                                        if (!value.length) {
                                            return Promise.reject();
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }) => {
                                const notifiers = (form.getFieldValue('notifiers') as NotifierOutput[]) || [];
                                const triggerName = form.getFieldValue(['trigger', 'name']);
                                const delegate = form.getFieldValue('delegate');

                                const dataSourceNotifier = notifierList.map((notifier) => {
                                    let capability = notifier.capabilities
                                        ? notifier.capabilities.some((capability) =>
                                              notificationCapability.includes(capability),
                                          )
                                        : undefined;

                                    const disabled =
                                        notifier.triggerExclude?.includes(triggerName) ||
                                        notifier.isDelegate !== delegate;

                                    if (['RunAnalyzer', 'RunResponder'].includes(notifier.name)) {
                                        capability = hasCortex && capability;
                                    }

                                    if (!disabled && notifier.name === 'RunResponder') {
                                        capability = capability && !isEmptyResponderList;
                                    }

                                    if (!disabled && notifier.name === 'RunAnalyzer') {
                                        capability = capability && !isEmptyAnalyzerList;
                                    }

                                    return {
                                        ...notifier,
                                        disabled: !capability || disabled,
                                    } as NotifiersMap;
                                });

                                return (
                                    <>
                                        <TitleSection
                                            extraAction={
                                                showNotifiers ? null : (
                                                    <AddButton
                                                        disabled={!dataSourceNotifier.length || !fields.length}
                                                        label={i('notification.form.notifiers.add-new')}
                                                        onClick={() => setShowNotifiers(true)}
                                                    />
                                                )
                                            }
                                        >
                                            {i('notification.form.notifiers.label')}
                                        </TitleSection>

                                        <div className="notification-manage__notifiers">
                                            {(showNotifiers || !fields.length) && (
                                                <THListChoice<NotifiersMap>
                                                    dataSource={dataSourceNotifier}
                                                    onChange={({ name }) => setActiveNotifier(name)}
                                                >
                                                    {({ name, label, imgSrc }) => (
                                                        <div>
                                                            <span className="notification-manage__notifiers-logo">
                                                                <Image src={imgSrc} preview={false} />
                                                            </span>
                                                            {label || name}
                                                        </div>
                                                    )}
                                                </THListChoice>
                                            )}

                                            {activeNotifier && (
                                                <NotifierManage
                                                    trigger={triggerName}
                                                    onClose={() => {
                                                        setShowNotifiers(false);
                                                        setActiveNotifier(undefined);
                                                    }}
                                                    visible={!!activeNotifier}
                                                    name={activeNotifier}
                                                    key={activeNotifier}
                                                    onVisibleChange={(visible) => {
                                                        if (visible) {
                                                            setFieldsTouched(true);
                                                        }
                                                    }}
                                                    onFinish={(notifier) => {
                                                        setShowNotifiers(false);
                                                        setActiveNotifier(undefined);
                                                        add({ ...notifier, enabled: true });
                                                    }}
                                                />
                                            )}

                                            <THSelector<NotifierOutput> dataSource={notifiers} disabled refKey="name">
                                                {({ name, enabled, ...values }, _, index = 0) => {
                                                    const notifier = notifierList.find(
                                                        ({ name: notifierName }) => notifierName === name,
                                                    );

                                                    const triggerName = form.getFieldValue(['trigger', 'name']);

                                                    const isDelegate = form.getFieldValue('delegate');

                                                    const disabled =
                                                        notifier?.triggerExclude?.includes(triggerName) ||
                                                        isDelegate !== notifier?.isDelegate ||
                                                        !notificationCapability.some((capability) =>
                                                            notifier?.capabilities?.includes(capability),
                                                        );

                                                    return (
                                                        <div
                                                            className={classList(
                                                                'notification-manage__notifiers__item',
                                                                { disabled: !enabled },
                                                            )}
                                                        >
                                                            <Space size="large">
                                                                <THForm.Item
                                                                    name={[index, 'enabled']}
                                                                    fieldKey={[index, 'enabled']}
                                                                    valuePropName="checked"
                                                                >
                                                                    <THSwitch disabled={disabled} />
                                                                </THForm.Item>

                                                                <div className="notification-manage__notifiers__item__name">
                                                                    <span className="notification-manage__notifiers__item__logo">
                                                                        <Image src={notifier?.imgSrc} preview={false} />
                                                                    </span>
                                                                    <span>
                                                                        {(values as any)?.endpoint
                                                                            ? `${name}/${(values as any).endpoint}`
                                                                            : notifier?.label || name}
                                                                    </span>
                                                                </div>
                                                            </Space>

                                                            <Space>
                                                                <NotifierManage
                                                                    key={name}
                                                                    name={name}
                                                                    trigger={triggerName}
                                                                    triggerElem={
                                                                        <THButton
                                                                            className="notification-manage__notifiers__item__button"
                                                                            type="link"
                                                                            disabled={disabled}
                                                                        >
                                                                            {i('general.edit')}
                                                                        </THButton>
                                                                    }
                                                                    defaultValues={notifiers[index]}
                                                                    onVisibleChange={(visible) => {
                                                                        if (visible) {
                                                                            setFieldsTouched(true);
                                                                        }
                                                                    }}
                                                                    onFinish={(notifier) =>
                                                                        form.setFieldsValue({
                                                                            notifiers: notifiers.map((item, indexMap) =>
                                                                                indexMap === index
                                                                                    ? { ...notifier, enabled }
                                                                                    : item,
                                                                            ),
                                                                        })
                                                                    }
                                                                />

                                                                <THButton
                                                                    className="notification-manage__notifiers__item__button"
                                                                    type="link"
                                                                    color="red"
                                                                    onClick={() => remove(index)}
                                                                >
                                                                    {i('general.delete')}
                                                                </THButton>
                                                            </Space>
                                                        </div>
                                                    );
                                                }}
                                            </THSelector>
                                        </div>
                                    </>
                                );
                            }}
                        </THForm.List>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
